import { useEffect, useRef, useState, type FC } from 'react';
import { NumericInput } from '@interstate/components/NumericInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { INPUT_INVALID_ERROR_MESSAGE } from '@makemydeal/dr-dash-components';

interface TermColumnHeaderProps {
    term: number;
    allTerms: number[];
    onTermChange: (newTerm: number) => void;
    isLoading: boolean;
    /** Flag the input (matches the main grid) when Payment Service returned no rates for this term. */
    hasError?: boolean;
    /** Make the input read-only (e.g. at max scenarios, when a structure can't be chosen). */
    readOnly?: boolean;
    testId?: string;
}

export const TermColumnHeader: FC<TermColumnHeaderProps> = ({
    term,
    allTerms,
    onTermChange,
    isLoading,
    hasError = false,
    readOnly = false,
    testId
}) => {
    const [editValue, setEditValue] = useState(String(term));
    const [resetKey, setResetKey] = useState(0);
    const isEditingRef = useRef(false);
    const isEscapingRef = useRef(false);
    // TextInputBase calls onChange on blur (to commit the formatted value). We set this flag
    // before reverting so that spurious onChange doesn't overwrite the reverted editValue.
    const isRevertingRef = useRef(false);

    useEffect(() => {
        if (!isEditingRef.current) {
            setEditValue(String(term));
        }
    }, [term]);

    const commitTerm = () => {
        isEditingRef.current = false;
        const enteredTerm = parseInt(editValue, 10);
        if (enteredTerm === term) return; // re-typed the same value — no-op

        // Commit any usable term and let Payment Service decide validity (an unsupported term returns
        // no rates and the column is flagged via the hasError prop — mirrors the main grid's
        // server-driven error). Only bail on non-numeric/empty input or a duplicate column, which
        // can't produce a meaningful payment; revert those to the prior value.
        const isDuplicate = allTerms.includes(enteredTerm);
        if (Number.isFinite(enteredTerm) && enteredTerm > 0 && !isDuplicate) {
            onTermChange(enteredTerm);
            return;
        }
        if (editValue !== String(term)) {
            isRevertingRef.current = true;
            setResetKey((k) => k + 1);
        }
        setEditValue(String(term));
    };

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        if (isRevertingRef.current) {
            isRevertingRef.current = false;
            return;
        }
        setEditValue(e.target.value);
    };

    const handleFocus = () => {
        isEditingRef.current = true;
        isRevertingRef.current = false;
    };

    const handleBlur = () => {
        if (isEscapingRef.current) {
            isEscapingRef.current = false;
            return;
        }
        commitTerm();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
            isEscapingRef.current = true;
            isEditingRef.current = false;
            if (editValue !== String(term)) {
                isRevertingRef.current = true;
                setEditValue(String(term));
                setResetKey((k) => k + 1);
            }
            (e.target as HTMLInputElement).blur();
        }
    };

    return (
        <NumericInput
            key={resetKey}
            size="small"
            displayLabel={false}
            label={`Term ${term} months`}
            name={`term-header-${term}`}
            autoInsertCommas={false}
            allowDecimal={false}
            disabled={isLoading}
            readOnly={readOnly}
            value={editValue}
            hasError={hasError}
            errorMessage={INPUT_INVALID_ERROR_MESSAGE}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            data-testid={testId}
        />
    );
};
